import isEqualWith from 'lodash/isEqualWith';
import { is } from 'immutable';
import { isImmutable } from 'app/utils/immutable';

type CallbackFuncType = (key: string, value: unknown, parentKeys: string[]) => void;
/**
 * Takes an object and callback, and recursively iterates through all keys in the object. Calls the
 * callback with the current nested key as the first argument, the current nested value as the
 * second argument, and an array of parent keys as the third argument.
 *
 * For example, `forEachKeyDeep({ a: { b: { c: 1 } } }, callback)` will call:
 *
 * ```
 * callback('a', { b: { c: 1 } }, []);
 * callback('b', { c: 1 }, ['a']);
 * callback('c', 1, ['a', 'b']);
 * ```
 *
 * Does not recurse into Arrays.
 */
export const forEachKeyDeep = (
  obj: { [index: string]: unknown },
  callback: CallbackFuncType,
  parentKeys: string[] = [],
) => {
  const recurse = (inner: unknown, keys: string[]) => {
    if (typeof inner === 'object' && inner && !Array.isArray(inner)) {
      Object.entries(inner).forEach(([key, value]) => {
        callback(key, value, keys);

        // Recurse
        if (typeof value === 'object') {
          recurse(value, [...keys, key]);
        }
      });
    }
  };

  recurse(obj, parentKeys);
};

export const isShallowEqual = (a: any, b: any) => {
  if (a === b) {
    return true;
  }

  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) {
    return false;
  }

  if (a instanceof Date && b instanceof Date) {
    return a.valueOf() === b.valueOf();
  } else if (a instanceof Date || b instanceof Date) {
    return false;
  }

  // Check if all values from a are same in b
  for (const name in a) { // eslint-disable-line no-restricted-syntax
    if (a[name] !== b[name]) {
      return false;
    }
  }

  // Check for any extra keys in b
  for (const name in b) { // eslint-disable-line no-restricted-syntax
    if (!(name in a)) {
      return false;
    }
  }

  return true;
};

/**
 * Check if two values are deeply equal to each other.
 *
 * Specifically useful to work around Immutable data which might be prohibitively large
 * or changeable to use with isEqual alone.
 */
export const isDeepEqual = (a: any, b: any) => (
  isEqualWith(a, b, (x: any, y: any) => {
    const xImmutable = isImmutable(x);
    const yImmutable = isImmutable(y);
    if (xImmutable && yImmutable) {
      return is(x, y);
    } else if (xImmutable !== yImmutable) {
      return false;
    }
  })
);

export function isUnknownObject(x: unknown): x is { [key: string]: unknown } {
  return x && typeof x === 'object';
}

export function isPromiseLike<T>(x: unknown): x is PromiseLike<T> {
  return isUnknownObject(x) && typeof x.then === 'function';
}

/**
 * Filter undesired values out of an object
 * @param obj the object to filter
 * @param isGood predicate to determine if a value is good.
 */
export function filterFieldsByValue<Good, Bad>(
  obj: {[index: string]: Good | Bad },
  isGood: (v: Good | Bad) => v is Good,
): {[index: string]: Good} {
  return Object.fromEntries(
    Object.entries(obj).filter((entry): entry is [string, Good] => isGood(entry[1])),
  );
}

export function filterUndefinedFields<Good>(
  obj: {[index: string]: Good | undefined },
): {[index: string]: Good} {
  const isGood = (x: Good | undefined): x is Good => {
    return !(x === undefined);
  };
  return filterFieldsByValue(obj, isGood);
}
